import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { NotificationKey } from 'helpers/app.config';
import { AxiosServiceResponse, PagedList } from 'interfaces/general';

const instance = axios.create({
  baseURL: `${process.env.MESSAGE_COMMUNICATION_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    // S./messageCommunication/typesNARIA CUANDO SE TENGA QA ESTABLE API KEY
    // 'Api-key': process.env.REQUEST_HEADER_API_KEY,
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export interface NotificationActionDto {
  actionKey: string;
  label: string;
  requiresComment: boolean;
  permissionKey?: string | null;
  /** Estatus destino configurado; usado por la campanita vía TicketApi (updateTicketStatus). */
  targetStatusId?: number | null;
}

export interface UnreadNotification {
  dateOccurred: string;
  emmitedDate: string;
  expireDate: string;
  iconUrl: string;
  isPublished: boolean;
  message: string;
  needsAttention?: boolean;
  notificationId: number;
  ticketId: number;
  title: string;
  advise?: string;
  userId: number;
  viewed?: boolean;
  notificationKey?: NotificationKey | string;
  ruleId?: number;
  entityType?: string;
  entityId?: number;
  actions?: NotificationActionDto[];
  objects?: string;
}

export interface ExecuteNotificationActionRequest {
  ruleId: number;
  actionKey: string;
  entityType: string;
  entityId: number;
  userNotificationId: number;
  comment?: string | null;
}

export type GetUnreadParams = {
  receiverUserId: number;
  text?: string;
  pageNumber: number;
  pageSize: number;
  orderColumn?: 'ASC' | 'DESC';
  descending?: boolean;
};

export type SendReadMessageBody = {
  receiverUserId: number;
  notificationIds: number[];
};

export interface MexicanPostalCodeRequest {
  stateName: string;
}

export interface MexicanPostalCodeResponse {
  responseDetail: [
    {
      countryStateName: string;
      totalDataExternal: number;
      totalDataDB: number;
      totalPostalCodesCreated: number;
      totalPostalCodesInactivated: number;
      totalMunicipalitiesCreated: number;
      totalLocalitiesCreated: number;
      totalPostalCodeAddressesUpdated: number;
    }
  ];
}

export interface GetMexicanPostalCodeResponse {
  key: string;
  value: string;
}

export interface SearchNotificationsRequest {
  text?: string;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  isRead?: boolean;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  services?: number[];
  clients?: number[];
  workforces?: number[];
}

export interface SearchNotificationResponse {
  id: number;
  ticketId: number;
  userFirstName: string;
  userFirstLastname: string;
  userSecondLastName: string;
  clientId: number;
  clientName: string;
  serviceId: number;
  serviceName: string;
  isRead: boolean;
  createdDateTime: Date;
  message: string;
  title: string;
}

export interface IncidentRequirementRequest {
  issueDescription: string;
  locationId: number | null;
  email: string;
  phone: string;
}

export interface NotificationsGeneralSearchParams {
  NotificationId?: number;
  Title?: string;
  Body?: string;
  LanguageId?: number;
  Text?: string;
  PageNumber?: number;
  PageSize?: number;
  OrderColumn?: string;
  Descending?: boolean;
}

export interface NotificationsGeneralSearchResponse {
  notificationId?: number;
  notificationKey?: string;
  title?: string;
  body?: string;
  languageId?: number;
}

export interface GetAllMessageParams {
  messageParamId?: number;
  name?: string;
}

export interface EditNotification {
  notificationId?: number;
  title?: string;
  body?: string;
  languageId?: number;
  messageParamsId?: number[];
  enabled?: boolean;
}

export type NotificationRuleTriggerTypeApi = 1 | 2 | 3 | 99;
export type NotificationRuleChannelApi = 1 | 2 | 3;
export type NotificationRuleRecipientTypeApi =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15;

export type NotificationRuleEntityTypeApi =
  | 'Ticket'
  | 'Shipment'
  | 'Tower'
  | 'Access'
  | 'Calendar';

export type NotificationRuleEventKeyApi =
  | 'TICKET_ASSIGNED'
  | 'TICKET_SLA_THRESHOLD'
  | 'TICKET_STATUS_CHANGED'
  | 'TICKET_CREATED'
  | 'TICKET_AWAY_ZONE'
  | 'TICKET_REMINDER'
  | 'TICKET_AUTO_CLOSE_REQUESTED'
  | 'SHIPMENT_REJECTED'
  | 'TOWER_DOCUMENT_EXPIRING'
  | 'ACCESS_BATCH_PERMISSIONS'
  | 'USER_ROLE_CHANGED'
  | 'CALENDAR_HOLIDAYS_CONFIGURED'
  | string;

export type NotificationRuleConditionOperator = 'eq' | 'in' | 'exists';
export type NotificationRuleFromStatusMode = 'Ignore' | 'MustMatch';
export type NotificationRuleOutcomeType = 'ChangeTicketStatus' | string;

export interface NotificationRuleConditionDto {
  key: string;
  operator: NotificationRuleConditionOperator;
  value: string | number | boolean | string[] | null;
}

export interface NotificationRuleActionDto {
  actionKey: string;
  label: string;
  outcomeType: NotificationRuleOutcomeType;
  targetStatusId?: number | null;
  fromStatusMode?: NotificationRuleFromStatusMode | null;
  fromStatusId?: number | null;
  requiresComment?: boolean;
  permissionKey?: string | null;
}

export interface NotificationRuleMetadataTrigger {
  triggerType: NotificationRuleTriggerTypeApi;
  name: string;
  defaultSubject: string;
  defaultBody: string;
  entityType?: NotificationRuleEntityTypeApi;
  eventKey?: NotificationRuleEventKeyApi;
}

export interface NotificationRuleMetadataVariable {
  key: string;
  token: string;
  description: string;
}

export interface NotificationRuleMetadataStatus {
  id: number;
  name: string;
}

export interface NotificationRuleMetadataChannel {
  channel: NotificationRuleChannelApi;
  name: string;
}

export interface NotificationRuleMetadataRecipientType {
  recipientType: NotificationRuleRecipientTypeApi;
  name: string;
  requiresRecipientId: boolean;
  requiresExternalEmail: boolean;
}

export type PendingSubtypeMatchRole = 'StatusTo' | 'StatusFrom' | 'Either';

export interface NotificationRulePendingSubtypeHint {
  statusId: number;
  /** Valores API: Serie | Warehouse | Scheduled */
  pendingSubtype: string;
  matchRole: PendingSubtypeMatchRole;
  label?: string;
}

export interface NotificationRuleConditionDefOption {
  value: string | number | boolean;
  label?: string;
  relatedStatusIds?: number[];
  relatedStatusFromIds?: number[];
}

/** Metadata Etapa 1 / compat (sin query). */
export interface NotificationRuleMetadata {
  triggers: NotificationRuleMetadataTrigger[];
  variables: NotificationRuleMetadataVariable[];
  statuses: NotificationRuleMetadataStatus[];
  channels: NotificationRuleMetadataChannel[];
  recipientTypes?: NotificationRuleMetadataRecipientType[];
  supportsRequiresAttention?: boolean;
  conditionDefs?: NotificationRuleConditionDef[];
  actionDefs?: NotificationRuleActionDefs;
  pendingSubtypeHints?: NotificationRulePendingSubtypeHint[];
  scopeFields?: string[];
  allowedChannels?: NotificationRuleChannelApi[];
  defaultSubject?: string;
  defaultBody?: string;
  canEditEntityOrEvent?: boolean;
  entityType?: NotificationRuleEntityTypeApi;
  eventKey?: string;
}

export interface NotificationRuleConditionDef {
  key: string;
  valueType: 'boolean' | 'string' | 'number' | string;
  options?: Array<
    string | number | boolean | NotificationRuleConditionDefOption
  >;
  name?: string;
}

export interface NotificationRuleActionDefs {
  outcomeTypes?: Array<{ outcomeType: string; name?: string }>;
  statuses?: NotificationRuleMetadataStatus[];
  supportsRequiresComment?: boolean;
}

export interface NotificationRuleCatalogEntityType {
  entityType: NotificationRuleEntityTypeApi;
  name: string;
}

export interface NotificationRuleCatalogEventKey {
  entityType: NotificationRuleEntityTypeApi;
  eventKey: string;
  name: string;
  triggerType: NotificationRuleTriggerTypeApi;
}

export interface NotificationRuleMetadataCatalog {
  entityTypes: NotificationRuleCatalogEntityType[];
  eventKeys: NotificationRuleCatalogEventKey[];
  channels: NotificationRuleMetadataChannel[];
  recipientTypes: NotificationRuleMetadataRecipientType[];
  outcomeTypes?: Array<{ outcomeType: string; name?: string }>;
  conditionKeys?: NotificationRuleConditionDef[];
  pendingSubtypeHints?: NotificationRulePendingSubtypeHint[];
  searchFilters?: string[];
}

export interface NotificationRuleMetadataParams {
  entityType?: string;
  eventKey?: string;
}

export interface NotificationRuleRecipientDto {
  recipientType: NotificationRuleRecipientTypeApi;
  recipientId?: number | null;
  externalEmail?: string | null;
  includeAdmins?: boolean;
}

export interface NotificationRuleDto {
  id: number;
  name: string;
  enabled: boolean;
  triggerType: NotificationRuleTriggerTypeApi;
  triggerTypeName: string;
  entityType?: NotificationRuleEntityTypeApi | string | null;
  eventKey?: string | null;
  requiresAttention?: boolean;
  conditions?: NotificationRuleConditionDto[];
  actions?: NotificationRuleActionDto[];
  slaPercentage?: number | null;
  statusFromId?: number | null;
  statusToId?: number | null;
  ignorePreviousStatus?: boolean;
  isReassignment?: boolean | null;
  subject: string;
  body: string;
  bodyPreview: string;
  channels: NotificationRuleChannelApi[];
  clientIds: number[];
  locationIds: number[];
  serviceIds: number[];
  groupIds: number[];
  userIds: number[];
  warehouseIds?: number[];
  recipients: NotificationRuleRecipientDto[];
  scopeSummary: string;
  recipientsSummary: string;
  isLegacy: boolean;
  legacyNotificationId?: number | null;
  createdDateTime?: string;
  updatedDateTime?: string;
}

export interface NotificationRuleSearchRequest {
  text?: string | null;
  enabled?: boolean | null;
  channel?: NotificationRuleChannelApi | null;
  channels?: NotificationRuleChannelApi[] | null;
  triggerType?: NotificationRuleTriggerTypeApi | null;
  entityType?: string | null;
  eventKey?: string | null;
  requiresAttention?: boolean | null;
  clientId?: number | null;
  locationId?: number | null;
  serviceId?: number | null;
  groupId?: number | null;
  userId?: number | null;
  recipientType?: NotificationRuleRecipientTypeApi | null;
  /** @deprecated Ignorado por BE Etapa 2. */
  includeLegacy?: boolean;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
}

export interface NotificationRuleUpsertRequest {
  name: string;
  enabled: boolean;
  triggerType: NotificationRuleTriggerTypeApi;
  entityType: string;
  eventKey: string;
  requiresAttention?: boolean;
  conditions?: NotificationRuleConditionDto[];
  actions?: NotificationRuleActionDto[];
  slaPercentage?: number | null;
  statusFromId?: number | null;
  statusToId?: number | null;
  ignorePreviousStatus?: boolean;
  isReassignment?: boolean | null;
  subject: string;
  body: string;
  channels: NotificationRuleChannelApi[];
  clientIds: number[];
  locationIds: number[];
  serviceIds: number[];
  groupIds: number[];
  userIds: number[];
  warehouseIds?: number[];
  recipients: NotificationRuleRecipientDto[];
}

export interface NotificationRuleToggleRequest {
  enabled?: boolean;
}

export interface ClientEmailRecipient {
  id: number;
  clientId: number;
  email: string;
  enabled: boolean;
  createdDateTime: string;
  updatedDateTime: string | null;
}

export interface GetClientEmailRecipientsParams {
  ClientId: number;
}

export interface PutClientEmailRecipientBody {
  clientId: number;
  emails: string[];
}

export default {
  instance,
  notifications: {
    /**
     * Retrieves unread notifications for a user.
     *
     * @tags Notifications
     * @name getUnread
     * @request GET: /api/v1/Notifications/me/GetUnread
     * @secure
     */
    getUnread: (params: GetUnreadParams, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<UnreadNotification>>
      >({
        url: `/Notifications/me/GetUnread`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Retrieves unread notifications for a user.
     *
     * @tags Notifications
     * @name getAllNotificationsByUser
     * @request GET: /api/v1/Notifications/Get
     * @secure
     */
    getAllNotificationsByUser: (
      params: unknown,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<UnreadNotification>>
      >({
        url: `/Notifications/Get`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Retrieves unread notifications for a user.
     *
     * @tags Notifications
     * @name deleteNotifications
     * @request GET: /api/v1/Notifications/Delete
     * @secure
     */
    deleteNotifications: (params: unknown, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<UnreadNotification>>
      >({
        url: `/Notifications/Delete`,
        method: 'DELETE',
        params,
        ...config
      }),

    /**
     * Send read message.
     *
     * @tags Notifications
     * @name sendReadMessage
     * @request PUT: /api/v1/Notifications/me/Read
     * @secure
     */
    sendReadMessage: (
      body: SendReadMessageBody,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<{ message: string }>>({
        url: `/Notifications/me/Read`,
        method: 'PUT',
        data: body,
        ...config
      }),

    /**
     * Ejecuta una acción de notificación del Configurador (campanita actions-first).
     *
     * @tags Notifications
     * @name executeNotificationAction
     * @request POST: /api/v1/notification-actions/execute
     * @secure
     */
    /**
     * @deprecated Cambios de estatus desde la campanita deben ir a TicketApi
     * (`updateTicketToScheduledStatus` / endpoints legacy / `updateTicketStatus`).
     * No usar este endpoint para ChangeTicketStatus.
     */
    executeNotificationAction: (
      body: ExecuteNotificationActionRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/notification-actions/execute`,
        method: 'POST',
        data: body,
        ...config
      }),

    /**
     * Recibe notificaciones de escalamiento de ticket .
     *
     * @tags Notifications
     * @name SearchEscalations
     * @request POST: /api/v1/Notifications/SearchEscalations
     * @secure
     */
    SearchEscalations: (
      data: SearchNotificationsRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<SearchNotificationResponse[]>>
      >({
        url: `/Notifications/SearchEscalations`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtiene los destinatarios de email del cliente
     *
     * @tags Notifications
     * @name getClientEmailRecipients
     * @request GET: /api/v1/Notifications/GetClientEmailRecipients
     * @secure
     */
    getClientEmailRecipients: (
      params: GetClientEmailRecipientsParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ClientEmailRecipient[]>>({
        url: `/Notifications/GetClientEmailRecipients`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Actualiza los destinatarios de email del cliente
     *
     * @tags Notifications
     * @name putClientEmailRecipient
     * @request PUT: /api/v1/Notifications/PutClientEmailRecipient
     * @secure
     */
    putClientEmailRecipient: (
      data: PutClientEmailRecipientBody,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Notifications/PutClientEmailRecipient`,
        method: 'PUT',
        data,
        ...config
      })
  },
  Locations: {
    /**
     * Nombre de los estados de Mexico externos a mexico
     *
     * @tags Location
     * @name MexicanPostalCode
     * @request POST: /api/v1/Location/MexicanPostalCode
     * @secure
     */
    MexicanPostalCode: (
      data: MexicanPostalCodeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<MexicanPostalCodeResponse>>({
        url: `/Location/MexicanPostalCode`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Nombre de los estados de Mexico externos a mexico
     *
     * @tags Location
     * @name ExternalMexicanStates
     * @request POST: /api/v1/Location/ExternalMexicanStates
     * @secure
     */
    ExternalMexicanStates: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<GetMexicanPostalCodeResponse[]>
      >({
        url: `/Location/ExternalMexicanStates`,
        method: 'POST',
        ...config
      })
  },
  Public: {
    /**
     * Solicitudes publicas de incidentes en ubcicaciones
     *
     * @tags Public
     * @name IncidentRequirement
     * @request POST: /api/v1/Public/IncidentRequirement
     * @secure
     */
    IncidentRequirement: (
      data: IncidentRequirementRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IncidentRequirementRequest>>({
        url: `/Public/IncidentRequirement`,
        method: 'POST',
        data,
        ...config
      })
  },
  getNotfications: {
    /**
     * Trae todas la notificaciones del sistema
     *
     * @tags Public
     * @name getAllNotifications
     * @request GET: /api/v1/Notifications/GeneralSearch
     * @secure
     */
    getAllNotifications: (
      params: NotificationsGeneralSearchParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<NotificationsGeneralSearchResponse>>
      >({
        url: `/Notifications/GeneralSearch`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Trae todos los parametros de las notificaciones
     *
     * @tags Public
     * @name getParamsNotificationById
     * @request GET: /api/v1/Notifications/GetMessageParamsById
     * @secure
     */
    getParamsNotificationById: (
      params: {
        NotificationId?: number;
        Text?: string;
        PageNumber?: number;
        PageSize?: number;
        OrderColumn?: string;
        Descending?: boolean;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<GetAllMessageParams>>
      >({
        url: `/Notifications/GetMessageParamsById`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Trae todos los parametros de las notificaciones
     *
     * @tags Public
     * @name getAllParamsNotification
     * @request GET: /api/v1/Notifications/GetAllMessageParams
     * @secure
     */
    getAllParamsNotification: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<GetAllMessageParams[]>>({
        url: `/Notifications/GetAllMessageParams`,
        method: 'GET',
        ...config
      }),
    /**
     * Edita la notificacion
     *
     * @tags Public
     * @name editNotification
     * @request PUT: /api/v1/Notifications/PutNotification
     * @secure
     */
    editNotification: (
      data: EditNotification,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Notifications/PutNotification`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Obtiene los destinatarios de email del cliente
     *
     * @tags Notifications
     * @name getClientEmailRecipients
     * @request GET: /api/v1/Notifications/GetClientEmailRecipients
     * @secure
     */
    getClientEmailRecipients: (
      params: GetClientEmailRecipientsParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ClientEmailRecipient[]>>({
        url: `/Notifications/GetClientEmailRecipients`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Actualiza los destinatarios de email del cliente
     *
     * @tags Notifications
     * @name putClientEmailRecipient
     * @request PUT: /api/v1/Notifications/PutClientEmailRecipient
     * @secure
     */
    putClientEmailRecipient: (
      data: PutClientEmailRecipientBody,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Notifications/PutClientEmailRecipient`,
        method: 'PUT',
        data,
        ...config
      })
  },
  notificationRules: {
    getMetadataCatalog: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<NotificationRuleMetadataCatalog>
      >({
        url: `/NotificationRules/metadata/catalog`,
        method: 'GET',
        ...config
      }),

    getMetadata: (
      params: NotificationRuleMetadataParams = {},
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<NotificationRuleMetadata>>({
        url: `/NotificationRules/metadata`,
        method: 'GET',
        params,
        ...config
      }),

    search: (
      data: NotificationRuleSearchRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<NotificationRuleDto>>
      >({
        url: `/NotificationRules/Search`,
        method: 'POST',
        data,
        ...config
      }),

    getById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<NotificationRuleDto>>({
        url: `/NotificationRules/${id}`,
        method: 'GET',
        ...config
      }),

    create: (
      data: NotificationRuleUpsertRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<NotificationRuleDto>>({
        url: `/NotificationRules`,
        method: 'POST',
        data,
        ...config
      }),

    update: (
      id: number,
      data: NotificationRuleUpsertRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<NotificationRuleDto>>({
        url: `/NotificationRules/${id}`,
        method: 'PUT',
        data,
        ...config
      }),

    remove: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<{ message: string }>>({
        url: `/NotificationRules/${id}`,
        method: 'DELETE',
        ...config
      }),

    duplicate: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<NotificationRuleDto>>({
        url: `/NotificationRules/${id}/duplicate`,
        method: 'POST',
        ...config
      }),

    toggle: (
      id: number,
      data: NotificationRuleToggleRequest = {},
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<NotificationRuleDto>>({
        url: `/NotificationRules/${id}/toggle`,
        method: 'PATCH',
        data,
        ...config
      })
  }
};
